import { DesktopPcmProbe } from "@/modules/audio-recording/visualizers/desktop-pcm-probe";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import type {
  MediaVisualizerEngine,
  MediaVisualizerStartOptions,
  MediaVisualizerStartResult,
} from "./types";

export class DesktopMediaVisualizer implements MediaVisualizerEngine {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private visualizer: DesktopPcmProbe | null = null;

  async start(options: MediaVisualizerStartOptions): Promise<MediaVisualizerStartResult> {
    const sampleRate = options.sampleRate ?? 16_000;
    const existingVisualizer = this.visualizer;
    if (existingVisualizer && this.audioContext && this.analyser) {
      return {
        audioContext: this.audioContext,
        analyser: this.analyser,
        visualizer: existingVisualizer,
      };
    }

    await this.stop();

    const AudioContextCtor: typeof AudioContext | undefined =
      typeof AudioContext !== "undefined"
        ? AudioContext
        : typeof window !== "undefined"
          ? // @ts-expect-error webkit prefixed fallback for older Electron/WebKit builds
            window.webkitAudioContext
          : undefined;

    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;

    if (AudioContextCtor) {
      audioContext = new AudioContextCtor({ sampleRate, latencyHint: "interactive" });
      analyser = audioContext.createAnalyser();
    }

    const visualizer = new DesktopPcmProbe();

    this.audioContext = audioContext;
    this.analyser = analyser;
    this.visualizer = visualizer;

    const audioContextForResult = audioContext ?? ({} as AudioContext);
    const analyserForResult = analyser ?? ({} as AnalyserNode);

    return {
      audioContext: audioContextForResult,
      analyser: analyserForResult,
      visualizer,
    };
  }

  async stop(): Promise<void> {
    this.visualizer?.dispose();
    this.visualizer = null;

    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        // ignore analyser disconnect failures
      } finally {
        this.analyser = null;
      }
    }

    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch {
        // ignore close failures
      } finally {
        this.audioContext = null;
      }
    }
  }

  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  getAnalyser(): AnalyserNode | null {
    return this.analyser;
  }

  getVisualizer(): AudioLevelProbe | null {
    return this.visualizer;
  }
}
